import numeral from 'numeral';
import { countCharacterAfterMatchingCase } from './generalHelpers';
import { isInteger, padEnd, padStart } from 'lodash';
import Fraction from 'fraction.js';

// #region colors
const validColors = [
  'black',
  'green',
  'white',
  'blue',
  'magenta',
  'yellow',
  'cyan',
  'red',
];

export const isValidNumberFormatColor = (extractedColor: string) =>
  validColors.find((color) => color === extractedColor);
// #endregion

//   #region match condition
export const matchNumberFormatCondition = (
  operator: string,
  conditionValue: string | number,
  comparedValue: string | number
) => {
  const value = numeral(comparedValue).value();
  if (value === null) return false;
  conditionValue = parseInt(conditionValue.toString(), 10);

  switch (operator) {
    case '<':
      return value < conditionValue;
    case '<=':
      return value <= conditionValue;
    case '>':
      return value > conditionValue;
    case '>=':
      return value >= conditionValue;
    case '=':
      return value === conditionValue;
  }

  return false;
};
//   #endregion

//   #region format number using code
export function formatNumberUsingCode(
  input: string | number,
  format: string,
  originalStr: string,
  locale: string
) {
  format = format.trim();
  const value = numeral(input).value();
  if (value === null) return originalStr;

  if (format.includes('/')) {
    return originalStr + formatFraction(value, format).toString();
  }

  let style = 'decimal';
  if (format.includes('%')) style = 'percent';

  const scientific = format.includes('E+');

  let [integerFormat, fractionFormat] = format.split('.');
  if (!fractionFormat) fractionFormat = integerFormat;

  //   TODO: In Future handle ? in depth to add space exactly where the ? mark is e.g #0.?0? "1. 2 "
  const optionalDecimalPlaces = fractionFormat.includes('?')
    ? fractionFormat.length
    : 0;
  const decimalPlaces = Math.max(
    optionalDecimalPlaces,
    countCharacterAfterMatchingCase(format, '.', '0#?')
  );

  let formattedValue = new Intl.NumberFormat(
    locale.length ? locale : undefined,
    {
      style: style,
      notation: scientific ? 'scientific' : 'standard',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
      minimumIntegerDigits: integerFormat.includes('?')
        ? integerFormat.length
        : 1,
      useGrouping: format.includes(','),
    }
  ).format(value);

  if (format.includes('(')) {
    formattedValue = formattedValue.replace('-', '');
    formattedValue = '(' + formattedValue + ')';
  }

  if (scientific) {
    const [val, power] = formattedValue.split('E');
    const zerosAfterE = countCharacterAfterMatchingCase(format, 'E+', '0');
    formattedValue = val + 'E+' + padStart(power, zerosAfterE, '0');
  }

  return originalStr + formattedValue;
}
//   #endregion

//   #region format fraction
function formatFraction(value: number, format: string) {
  if (isInteger(value)) return value;
  let str = '';

  if (format.includes(' ')) {
    format = format.split(' ')[1];
  }

  const [numeratorFormat, denominatorFormat] = format.split('/');

  let fraction = new Fraction(value).toFraction(true);

  let fractionPart = fraction;
  if (fraction.includes(' ')) {
    const results = fraction.split(' ');
    str = `${results[0]} `;
    fractionPart = results[1];
  }

  if (fractionPart) {
    const [n, d] = fractionPart.split('/');
    str += formatAndPad(numeratorFormat, n);
    str += '/';
    str += formatAndPad(denominatorFormat, d);
  }

  return str;
}

function formatAndPad(formatCode: string, value: number | string) {
  let formattedStr = numeral(value).format(formatCode);
  if (formatCode.includes('?') && formattedStr.length < formatCode.length) {
    formattedStr = padEnd(formattedStr, formatCode.length, ' ');
  }
  return formattedStr;
}
//   #endregion

// #region format column
export function formatColumn(
  columnWidth: number,
  fontSize: number,
  font: string,
  initialPart: string,
  fillCharacter = ' ',
  lastPart: string
): string {
  const totalWidth = columnWidth;

  // Calculate the width of initial and last parts based on font size and family
  const initialWidth = getWidth(initialPart, fontSize, font);
  const lastWidth = getWidth(lastPart, fontSize, font);

  const characterWidth = getWidth(fillCharacter, fontSize, font);
  const availableWidth = totalWidth - initialWidth - lastWidth;

  // Calculate the remaining space and fill it with fillValue if needed
  const fillSpace = fillCharacter.repeat(
    Math.max(0, availableWidth / characterWidth)
  );

  // Construct the result string
  const result = `${initialPart}${fillSpace}${lastPart}`;

  return result;
}

function getWidth(text: string, fontSize: number, font: string): number {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    // Canvas not supported
    return text.length * fontSize;
  }

  context.font = `${fontSize}px ${font}`;
  return context.measureText(text).width;
}
//   #endregion

// Experiment Code with Currency:
// const isCurrency = !originalStr.includes('[PAD]') && locale.length;
// if (locale && isCurrency) style = 'currency';
//   const [integerVal, fractionVal] = value.toString().split('.');

//   Replace Currency If already added and there is not PAD
// This will be useful in case of arabic or symbol that comes after formatting
// let currencyCode = '';
// if (isCurrency) {
//   let symbol = '';
//   currencyCode = localesToCurrency[locale];
//   if (currencyCode) {
//     symbol = getCurrenySymbolFromCode(currencyCode);
//   } else symbol = '$';
//   originalStr = originalStr.replaceAll(symbol, '');
// }

//   console.log('currencyCode', currencyCode, locale);
